type LogLevel = "debug" | "info" | "warn" | "error" | "none";
type ForwardSeverity = "warn" | "error";

const levelPriority: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
};

const modeDefault: LogLevel =
  (import.meta.env.MODE ?? "").toLowerCase() === "production" ? "warn" : "debug";
const envLevel = (import.meta.env.VITE_LOG_LEVEL as string | undefined) ?? null;
const DEFAULT_LEVEL: LogLevel = parseLevel(envLevel) ?? modeDefault;
const STORAGE_KEY = "lotlocator.logLevel";
const FORWARD_ERRORS =
  ((import.meta.env.VITE_CLIENT_LOG_FORWARD as string | undefined) ?? "true").toLowerCase() === "true";

let reporterModule: typeof import("../services/clientErrorReporter") | null = null;
let reporterLoadFailed = false;

function parseLevel(raw: string | null | undefined): LogLevel | null {
  if (!raw) return null;
  const normalized = raw.toLowerCase();
  if (normalized === "debug" || normalized === "info" || normalized === "warn") {
    return normalized;
  }
  if (normalized === "error") return "error";
  if (normalized === "none" || normalized === "off" || normalized === "silent") {
    return "none";
  }
  return null;
}

function resolveLevel(): LogLevel {
  let level = DEFAULT_LEVEL;
  if (typeof window === "undefined") {
    return level;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const queryLevel =
      parseLevel(params.get("logLevel")) ?? parseLevel(params.get("loglevel"));
    if (queryLevel) {
      window.localStorage?.setItem(STORAGE_KEY, queryLevel);
      return queryLevel;
    }

    const storedLevel = parseLevel(window.localStorage?.getItem(STORAGE_KEY));
    if (storedLevel) {
      level = storedLevel;
    }
  } catch {
    // ignore parsing/storage errors
  }

  return level;
}

async function forwardClientError(namespace: string, severity: ForwardSeverity, args: unknown[]) {
  if (!FORWARD_ERRORS || typeof window === "undefined" || reporterLoadFailed) {
    return;
  }

  try {
    if (!reporterModule) {
      reporterModule = await import("../services/clientErrorReporter");
    }
    if (!reporterModule?.reportClientError) {
      reporterLoadFailed = true;
      return;
    }

    const payload = buildForwardPayload(namespace, severity, args);
    if (payload) {
      reporterModule.reportClientError(payload.error, payload.meta);
    }
  } catch {
    reporterLoadFailed = true;
  }
}

function buildForwardPayload(namespace: string, severity: ForwardSeverity, args: unknown[]) {
  const errorArg = args.find((arg) => arg instanceof Error) as Error | undefined;
  const stringArg = args.find((arg) => typeof arg === "string") as string | undefined;

  const message = (errorArg?.message ?? stringArg ?? "").trim();
  if (!message) {
    return null;
  }

  const safeStack = typeof errorArg?.stack === "string" ? errorArg.stack : undefined;

  return {
    error: errorArg ?? message,
    meta: {
      severity,
      name: errorArg?.name ?? `logger.${namespace}`,
      stack: safeStack,
      fingerprint: `logger:${namespace}:${message.slice(0, 80)}`,
      tags: {
        namespace,
        level: severity,
        forwarded: true
      }
    }
  };
}

export type Logger = {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  level: LogLevel;
};

export function createLogger(namespace: string): Logger {
  const threshold = resolveLevel();

  const shouldLog = (level: LogLevel) =>
    levelPriority[level] >= levelPriority[threshold] && threshold !== "none";

  const makeLog =
    (level: "debug" | "info" | "warn" | "error") =>
    (...args: unknown[]) => {
      if (!shouldLog(level)) return;
      console[level](`[${namespace}]`, ...args);
      if (levelPriority[level] >= levelPriority.error) {
        void forwardClientError(namespace, level === "warn" ? "warn" : "error", args);
      }
    };

  return {
    debug: makeLog("debug"),
    info: makeLog("info"),
    warn: makeLog("warn"),
    error: makeLog("error"),
    level: threshold,
  };
}
